import { ReactNode } from 'react';
import type { ElementType } from 'react';

import { cn } from '../../lib/utils';
import {
  DropdownMenu as ShadDropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './shadcn/dropdown-menu';

interface DropdownProps {
  isOpen?: boolean;
  toggle?: () => void;
  children: ReactNode;
  direction?: 'down' | 'up' | 'start' | 'end';
  className?: string;
}

interface DropdownToggleProps {
  children: ReactNode;
  className?: string;
  tag?: ElementType;
  [key: string]: any;
}

interface DropdownMenuProps {
  children: ReactNode;
  className?: string;
  end?: boolean;
  direction?: 'down' | 'up' | 'start' | 'end';
  [key: string]: any;
}

interface DropdownItemProps {
  children?: ReactNode;
  className?: string;
  onClick?: () => void;
  href?: string;
  divider?: boolean;
  header?: boolean;
  [key: string]: any;
}

export function Dropdown({ children, className = '', isOpen, toggle, direction }: DropdownProps) {
  const controlled = typeof isOpen === 'boolean';
  const handleOpenChange = (open: boolean) => {
    if (toggle) toggle();
  };

  return (
    <ShadDropdownMenu
      open={controlled ? isOpen : undefined}
      onOpenChange={toggle ? handleOpenChange : undefined}
    >
      <div className={cn('dropdown', className)} data-direction={direction}>
        {children}
      </div>
    </ShadDropdownMenu>
  );
}

export function DropdownToggle({ 
  children, 
  className = '', 
  tag = 'button',
  ...props 
}: DropdownToggleProps) {
  const Tag: ElementType = tag;
  const isButton = Tag === 'button';
  
  return (
    <DropdownMenuTrigger asChild>
      <Tag
        className={cn('dropdown-toggle', className)}
        {...(isButton && props.type === undefined ? { type: 'button' } : {})}
        {...props}
      >
        {children}
      </Tag>
    </DropdownMenuTrigger>
  );
}

export function DropdownMenu({ 
  children, 
  className = '',
  end = false,
  direction,
  ...props 
}: DropdownMenuProps) {
  const isEnd = end || className.includes('dropdown-menu-end');
  const { style, ...restProps } = props;
  const safeBoundary = typeof document === 'undefined' ? undefined : document.body;
  const safeStyle = {
    zIndex: 2000,
    maxWidth: 'calc(100vw - 1rem)',
    width: 'max-content',
    ...style,
  };
  const side = (() => {
    if (direction === 'up') return 'top';
    if (direction === 'start') return 'left';
    if (direction === 'end') return 'right';
    return 'bottom';
  })();
  
  return (
    <DropdownMenuContent
      align={isEnd ? 'end' : 'start'}
      side={side}
      avoidCollisions
      sideOffset={4}
      collisionPadding={8}
      collisionBoundary={safeBoundary}
      className={cn('dropdown-menu show', className)}
      style={safeStyle}
      {...restProps}
    >
      {children}
    </DropdownMenuContent>
  );
}

export function DropdownItem({ 
  children, 
  className = '',
  onClick,
  href,
  divider = false,
  header = false,
  ...props 
}: DropdownItemProps) {
  if (divider) {
    return <DropdownMenuSeparator className="dropdown-divider" />;
  }

  if (header) {
    return (
      <DropdownMenuLabel className={cn('dropdown-header', className)} {...props}>
        {children}
      </DropdownMenuLabel>
    );
  }

  if (href) {
    return (
      <DropdownMenuItem asChild className={cn('dropdown-item', className)} {...props}>
        <a href={href} onClick={onClick}>
          {children}
        </a>
      </DropdownMenuItem>
    );
  }

  return (
    <DropdownMenuItem className={cn('dropdown-item', className)} onClick={onClick} {...props}>
      {children}
    </DropdownMenuItem>
  );
}

export function UncontrolledDropdown({ 
  children, 
  className = '' 
}: DropdownProps) {
  return (
    <ShadDropdownMenu>
      <div className={cn('dropdown', className)}>{children}</div>
    </ShadDropdownMenu>
  );
}
